import React, { useState } from "react";

import {
  Button,
  Checkbox,
  Grid,
  LinearProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TablePagination,
  TableRow,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import { useParams } from "react-router-dom";

import { Inspection } from "@safetyculture/s12-apis-connect-web/s12/inspections/v1/inspection_pb";
import { Template } from "@safetyculture/s12-apis-connect-web/s12/templates/v1/template_pb";
import { Region } from "@safetyculture/s12-apis-connect-web/s12/tools/support/v1/workflows_pb";

import {
  UserInspectionsList,
  UserTemplatesList,
} from "../../../context/user-templates-and-inspections-context";
import mainStyles from "../../../styles";
import { UserUrlParamTypes } from "../../../types/user-display";
import RPCTablePaginationActions from "../../shared/table/rpc-table-pagination-actions";
import BulkTransferDocumentsDialog from "./components/bulk-transfer-documents-dialog";
import TransferDocumentsDialog from "./components/transfer-documents-dialog";
import UserTemplatesAndInspectionsRecord from "./components/user-templates-and-inspections-record";

interface UserTemplatesAndInspectionsListProps {
  getUserItemsList: (ownerId: string, pageSize: number, offset: number) => void;
  documentType: string;
  totalItems: number;
  pageSize: number;
  offset: number;
  userItemsList: UserInspectionsList | UserTemplatesList;
  transferDocuments: (toID: string, documentIDs: string[]) => void;
  searchArchived: boolean;
  setSearchArchived: (includeArchived: boolean) => void;
  restoreTemplateVersion: (inspectionId: string, targetUserId: string) => void;
  copyTemplate: (
    templateId: string,
    targetUserId: string,
    targetOrgId: string,
    targetRegion: Region
  ) => void;
  orgId: string;
}

const UserTemplatesAndInspectionsList = ({
  getUserItemsList,
  documentType,
  totalItems,
  pageSize,
  offset,
  userItemsList,
  transferDocuments,
  searchArchived,
  setSearchArchived,
  restoreTemplateVersion,
  copyTemplate,
  orgId,
}: UserTemplatesAndInspectionsListProps) => {
  const { userId } = useParams<UserUrlParamTypes>();

  const classes = mainStyles();

  const [selectedAll, setSelectedAll] = useState(false);
  const [selectedDocuments, setSelectedDocuments] = useState<string[]>([]);
  const [transferPopupOpen, setTransferPopupOpen] = useState(false);
  const [bulkTransferModalOpen, setBulkTransferModalOpen] = useState(false);

  const populateDocumentRecords = () => {
    if (documentType == "inspection") {
      if (
        (userItemsList as UserInspectionsList).userInspectionsListResponse
          .inspections.length == 0
      ) {
        return (
          <TableRow>
            <TableCell colSpan={7}>
              <b>{"This user doesn't have any inspections."}</b>
            </TableCell>
          </TableRow>
        );
      }

      return (
        userItemsList as UserInspectionsList
      ).userInspectionsListResponse.inspections.map(
        (row: Inspection, index: number) => (
          <UserTemplatesAndInspectionsRecord
            selected={selectedDocuments.indexOf(row.id) !== -1}
            setSelected={(selected) => {
              if (selected) {
                if (selectedDocuments.indexOf(row.id) === -1) {
                  setSelectedDocuments([...selectedDocuments, row.id]);
                }
              } else {
                setSelectedDocuments(
                  selectedDocuments.filter((docId) => docId !== row.id)
                );
                setSelectedAll(selected);
              }
            }}
            row={row}
            key={index}
            documentType={documentType}
            restoreTemplateVersion={restoreTemplateVersion}
            copyTemplate={copyTemplate}
          />
        )
      );
    }
    if (
      (userItemsList as UserTemplatesList).userTemplatesListResponse.templates
        .length == 0
    ) {
      return (
        <TableRow>
          <TableCell colSpan={7}>
            <b>{"This user doesn't have any templates."}</b>
          </TableCell>
        </TableRow>
      );
    }
    return (
      userItemsList as UserTemplatesList
    ).userTemplatesListResponse.templates.map(
      (row: Template, index: number) => (
        <UserTemplatesAndInspectionsRecord
          selected={selectedDocuments.indexOf(row.id) !== -1}
          setSelected={(selected) => {
            if (selected) {
              if (selectedDocuments.indexOf(row.id) === -1) {
                setSelectedDocuments([...selectedDocuments, row.id]);
              }
            } else {
              setSelectedDocuments(
                selectedDocuments.filter((docId) => docId !== row.id)
              );
              setSelectedAll(selected);
            }
          }}
          row={row}
          key={index}
          documentType={documentType}
          restoreTemplateVersion={restoreTemplateVersion}
          copyTemplate={copyTemplate}
        />
      )
    );
  };

  const handleSelectAllClick = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSelectedAll(event.target.checked);
    if (event.target.checked) {
      let allUserItems = [];
      if (documentType == "inspection") {
        allUserItems = (
          userItemsList as UserInspectionsList
        ).userInspectionsListResponse.inspections.map((e) => e.id);
      } else {
        allUserItems = (
          userItemsList as UserTemplatesList
        ).userTemplatesListResponse.templates.map((e) => e.id);
      }
      setSelectedDocuments(allUserItems);
      return;
    }
    setSelectedDocuments([]);
  };

  const handleChangePage = (
    _event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number
  ) => {
    setSelectedAll(false);
    const newOffset = newPage * userItemsList.pagination.pageSize;
    handleResultsPageUpdate(newOffset);
  };

  const handleResultsPageUpdate = (newOffset: number) => {
    getUserItemsList(userId, userItemsList.pagination.pageSize, newOffset);
  };

  const transferDocumentsClick = (toID: string) => {
    setSelectedDocuments([]);
    transferDocuments(toID, selectedDocuments);
  };

  const docName = documentType == "template" ? "Templates" : "Inspections";

  return (
    <Grid container sx={classes.paddingTopSmall}>
      <TableContainer component={Paper}>
        <Table aria-label="user-list-table">
          <TableHead>
            <TableRow>
              <TableCell align="right" colSpan={4}>
                <ToggleButtonGroup
                  value={searchArchived ? "archivedDocuments" : "documents"}
                  exclusive
                  onChange={(event, value) => {
                    setSearchArchived(value === "archivedDocuments");
                  }}
                  size="small"
                >
                  <ToggleButton
                    value="documents"
                    data-testid={`${documentType}-only-toggle`}
                  >
                    {documentType + "s"}
                  </ToggleButton>
                  <ToggleButton
                    value="archivedDocuments"
                    data-testid={`${documentType}-archived-toggle`}
                  >
                    {"Archived " + documentType + "s"}
                  </ToggleButton>
                </ToggleButtonGroup>
              </TableCell>
              <TableCell align="right" colSpan={3}>
                <>
                  <Button
                    variant="contained"
                    onClick={() => setTransferPopupOpen(true)}
                    disabled={selectedDocuments.length <= 0}
                    data-testid={`move-selected-${documentType}-btn`}
                  >
                    Move {selectedDocuments.length} Selected {docName}
                  </Button>
                  <Button
                    variant="contained"
                    onClick={() => {
                      setBulkTransferModalOpen(true);
                    }}
                    disabled={totalItems === 0 || selectedDocuments.length > 0}
                    data-testid="bulk-transfer-documents-btn"
                    sx={classes.spaceDocumentTransferButtons}
                  >
                    Move All {docName}
                  </Button>
                </>
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell>
                <Checkbox
                  checked={selectedAll}
                  disabled={totalItems === 0}
                  onChange={handleSelectAllClick}
                  inputProps={
                    {
                      "data-testid": `user-document-${documentType}-selected-all`,
                      // eslint-disable-next-line
                    } as any
                  }
                />
              </TableCell>
              <TableCell style={{ width: "25%" }}>
                <b>Title</b>
              </TableCell>
              <TableCell colSpan={1}>
                <b>Date Started</b>
              </TableCell>
              <TableCell colSpan={1}>
                <b>Date Modified</b>
              </TableCell>
              {documentType == "inspection" && (
                <TableCell colSpan={1}>
                  <b>Inspection ID</b>
                </TableCell>
              )}
              {documentType == "template" && (
                <TableCell colSpan={1}>
                  <b>Archived</b>
                </TableCell>
              )}
              <TableCell colSpan={1}>
                <b>Template ID</b>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {userItemsList.isLoading ? (
              <TableRow>
                <TableCell colSpan={7}>
                  <LinearProgress />
                </TableCell>
              </TableRow>
            ) : (
              populateDocumentRecords()
            )}
          </TableBody>
          <TableFooter>
            <TableCell padding="checkbox" style={{ width: "2.5%" }} />
            <TableCell colSpan={7} padding="none">
              <table style={{ width: "100%" }}>
                <tbody>
                  <TablePagination
                    rowsPerPageOptions={[pageSize]}
                    count={totalItems}
                    rowsPerPage={pageSize}
                    page={Math.round(offset / pageSize)}
                    component="td"
                    onPageChange={handleChangePage}
                    ActionsComponent={RPCTablePaginationActions}
                  />
                </tbody>
              </table>
            </TableCell>
          </TableFooter>
        </Table>
      </TableContainer>
      <TransferDocumentsDialog
        open={transferPopupOpen}
        setOpen={setTransferPopupOpen}
        transferDocuments={transferDocumentsClick}
        isInspection={documentType === "inspection"}
        numDocuments={selectedDocuments.length}
      />
      <BulkTransferDocumentsDialog
        open={bulkTransferModalOpen}
        setOpen={setBulkTransferModalOpen}
        isInspection={documentType === "inspection"}
        searchArchived={searchArchived}
        getUserItemsList={getUserItemsList}
        orgId={orgId}
      />
    </Grid>
  );
};
export default UserTemplatesAndInspectionsList;
